/* eslint-disable max-len */
import { getStack } from '@nuskin/contentstack-lib';

const CONTENT_TYPE_UIDS = ['shipping_component']

const DEFAULT_LABELS = {
  address1: 'Street Address',
  address2: 'Apartment, suite, etc.',
  city: 'City',
  district: 'District',
  region: 'Region',
  postalCode: 'Postal Code',
  invalidEmail: 'Invalid email address.',
  fieldRequired: 'This field is required.',
  illegalCharacters: 'Contains invalid characters.',
  invalidPostalCode: 'Invalid postal code.',
  rateLimited: 'Too many requests. Please wait a moment and try again.',
  lookupError: 'We could not look up that address right now.',
  validationError: 'We could not validate that address right now.',
  loadingAddressForm: 'Loading address form...',
  switchToAutocomplete: 'Switch to Autocomplete',
  switchToManualEntry: 'Switch to Manual Entry',
  addressLocation: 'Address location',
  openInMaps: 'Open in Google Maps',
  usVerified: 'Verified',
  usVerifiedMessage: 'This address is fully verified.',
  usMissingSecondaryTitle: 'Apartment or suite needed',
  usMissingSecondaryMessage: 'The main address is valid, but a unit number is required.',
  usInvalidSecondaryRequiredTitle: 'Apartment or suite not recognized',
  usInvalidSecondaryRequiredMessage: 'The street address is valid, but the unit number was not recognized and is required.',
  usInvalidSecondaryOptionalTitle: 'Unit not recognized',
  usInvalidSecondaryOptionalMessage: 'The main address is valid, but the unit number was not recognized.',
  usVacantTitle: 'Verified, but marked vacant',
  usVacantMessage: 'This address is valid but marked as vacant.',
  usNoStatTitle: 'Verified with caution',
  usNoStatMessage: 'This address is valid, but USPS indicates limited delivery status.',
  usNotConfirmedTitle: 'Not verified',
  usNotConfirmedMessage: 'This address could not be confirmed.',
  usNoResults: 'We couldn’t verify this address. Check the address and try again.',
  intlVerifiedTitle: 'Verified',
  intlVerifiedMessage: 'This address is verified.',
  intlVerifiedSubBuildingChangedTitle: 'Verified with unit changes',
  intlVerifiedSubBuildingChangedMessage: 'This address is verified, but the apartment or suite information was changed.',
  intlPartialTitle: 'Partially verified',
  intlPartialMessage: 'This address could only be partially verified.',
  intlAmbiguousTitle: 'Multiple matches found',
  intlAmbiguousMessage: 'Multiple possible matches were found for this address.',
  intlNotVerifiedTitle: 'Not verified',
  intlNotVerifiedMessage: 'This address could not be verified.',
  intlNoResults: 'We couldn’t verify this address. Check the address and try again.'
};

const cache = {};

const mergeResultEntries = (results = []) => {
  const entries = Array.isArray(results) ? results : [results];
  return entries.reduce((acc, entry) => ({ ...acc, ...(entry || {}) }), {});
};

const getUsValidationStrings = (source = {}) => source.smartyUsValidationStrings || {};

const getIntlValidationStrings = (source = {}) => source.smartyIntlValidationStrings || {};

const mapFieldLabels = (source = {}) => {
  const usValidationStrings = getUsValidationStrings(source);
  const intlValidationStrings = getIntlValidationStrings(source);

  return {
    address1: source.address1Label || DEFAULT_LABELS.address1,
    address2: source.address2Label || DEFAULT_LABELS.address2,
    city: source.cityLabel || DEFAULT_LABELS.city,
    district: source.districtLabel || DEFAULT_LABELS.district,
    region: source.regionLabel || DEFAULT_LABELS.region,
    postalCode: source.postalCodeLabel || DEFAULT_LABELS.postalCode,
    invalidEmail: source.invalidEmail || DEFAULT_LABELS.invalidEmail,
    fieldRequired: source.fieldRequired || DEFAULT_LABELS.fieldRequired,
    illegalCharacters: source.illegalCharacters || DEFAULT_LABELS.illegalCharacters,
    invalidPostalCode: source.invalidPostalCode || DEFAULT_LABELS.invalidPostalCode,
    rateLimited: source.rateLimited || DEFAULT_LABELS.rateLimited,
    lookupError: source.lookupError || DEFAULT_LABELS.lookupError,
    validationError: source.validationError || DEFAULT_LABELS.validationError,
    loadingAddressForm: source.loadingAddressForm || DEFAULT_LABELS.loadingAddressForm,
    switchToAutocomplete: source.switchToAutocomplete || DEFAULT_LABELS.switchToAutocomplete,
    switchToManualEntry: source.switchToManuelEntry || DEFAULT_LABELS.switchToManualEntry,
    addressLocation: source.addressLocation || DEFAULT_LABELS.addressLocation,
    openInMaps: source.openInMaps || DEFAULT_LABELS.openInMaps,
    usVerified: usValidationStrings.usVerified || source.usVerified || DEFAULT_LABELS.usVerified,
    usVerifiedMessage: usValidationStrings.usVerifiedMessage || source.usVerifiedMessage || DEFAULT_LABELS.usVerifiedMessage,
    usMissingSecondaryTitle: usValidationStrings.usMissingSecondaryTitle || source.usMissingSecondaryTitle || DEFAULT_LABELS.usMissingSecondaryTitle,
    usMissingSecondaryMessage: usValidationStrings.usMissingSecondaryMessage || source.usMissingSecondaryMessage || DEFAULT_LABELS.usMissingSecondaryMessage,
    usInvalidSecondaryRequiredTitle: usValidationStrings.usInvalidSecondaryRequiredTitle || source.usInvalidSecondaryRequiredTitle || DEFAULT_LABELS.usInvalidSecondaryRequiredTitle,
    usInvalidSecondaryRequiredMessage: usValidationStrings.usInvalidSecondaryRequiredMessage || source.usInvalidSecondaryRequiredMessage || DEFAULT_LABELS.usInvalidSecondaryRequiredMessage,
    usInvalidSecondaryOptionalTitle: usValidationStrings.usInvalidSecondaryOptionalTitle || source.usInvalidSecondaryOptionalTitle || DEFAULT_LABELS.usInvalidSecondaryOptionalTitle,
    usInvalidSecondaryOptionalMessage: usValidationStrings.usInvalidSecondaryOptionalMessage || source.usInvalidSecondaryOptionalMessage || DEFAULT_LABELS.usInvalidSecondaryOptionalMessage,
    usVacantTitle: usValidationStrings.usVacantTitle || source.usVacantTitle || DEFAULT_LABELS.usVacantTitle,
    usVacantMessage: usValidationStrings.usVacantMessage || source.usVacantMessage || DEFAULT_LABELS.usVacantMessage,
    usNoStatTitle: usValidationStrings.usNoStatTitle || source.usNoStatTitle || DEFAULT_LABELS.usNoStatTitle,
    usNoStatMessage: usValidationStrings.usNoStatMessage || source.usNoStatMessage || DEFAULT_LABELS.usNoStatMessage,
    usNotConfirmedTitle: usValidationStrings.usNotConfirmedTitle || source.usNotConfirmedTitle || DEFAULT_LABELS.usNotConfirmedTitle,
    usNotConfirmedMessage: usValidationStrings.usNotConfirmedMessage || source.usNotConfirmedMessage || DEFAULT_LABELS.usNotConfirmedMessage,
    usNoResults: usValidationStrings.usNoResults || source.usNoResults || DEFAULT_LABELS.usNoResults,
    intlVerifiedTitle: intlValidationStrings.intlVerifiedTitle || source.intlVerifiedTitle || DEFAULT_LABELS.intlVerifiedTitle,
    intlVerifiedMessage: intlValidationStrings.intlVerifiedMessage || source.intlVerifiedMessage || DEFAULT_LABELS.intlVerifiedMessage,
    intlVerifiedSubBuildingChangedTitle: intlValidationStrings.intlVerifiedSubBuildingChangedTitle || source.intlVerifiedSubBuildingChangedTitle || DEFAULT_LABELS.intlVerifiedSubBuildingChangedTitle,
    intlVerifiedSubBuildingChangedMessage: intlValidationStrings.intlVerifiedSubBuildingChangedMessage || source.intlVerifiedSubBuildingChangedMessage || DEFAULT_LABELS.intlVerifiedSubBuildingChangedMessage,
    intlPartialTitle: intlValidationStrings.intlPartialTitle || source.intlPartialTitle || DEFAULT_LABELS.intlPartialTitle,
    intlPartialMessage: intlValidationStrings.intlPartialMessage || source.intlPartialMessage || DEFAULT_LABELS.intlPartialMessage,
    intlAmbiguousTitle: intlValidationStrings.intlAmbiguousTitle || source.intlAmbiguousTitle || DEFAULT_LABELS.intlAmbiguousTitle,
    intlAmbiguousMessage: intlValidationStrings.intlAmbiguousMessage || source.intlAmbiguousMessage || DEFAULT_LABELS.intlAmbiguousMessage,
    intlNotVerifiedTitle: intlValidationStrings.intlNotVerifiedTitle || source.intlNotVerifiedTitle || DEFAULT_LABELS.intlNotVerifiedTitle,
    intlNotVerifiedMessage: intlValidationStrings.intlNotVerifiedMessage || source.intlNotVerifiedMessage || DEFAULT_LABELS.intlNotVerifiedMessage,
    intlNoResults: intlValidationStrings.intlNoResults || source.intlNoResults || DEFAULT_LABELS.intlNoResults
  };
};

export const normalizeAddressFieldLabels = (source = {}) => {
  return {
    ...DEFAULT_LABELS,
    ...mapFieldLabels(source || {})
  };
};

export const getAddressFieldLabels = async ({ country, language }) => {
  const countryCode = country || 'US';
  const locale = language || 'en';
  const cacheKey = `${locale}_${countryCode}`;

  if (cache[cacheKey]) {
    return cache[cacheKey];
  }

  try {
    const stack = getStack();
    const results = await stack.getSingletonEntries({
      contentTypeUIDs: CONTENT_TYPE_UIDS,
      country: countryCode,
      language: locale,
      camelcase: true,
      mergeWithFallback: true
    });

    const merged = mergeResultEntries(results);
    const labels = normalizeAddressFieldLabels(merged);
    cache[cacheKey] = labels;
    return labels;
  } catch (err) {
    cache[cacheKey] = DEFAULT_LABELS;
    return DEFAULT_LABELS;
  }
};

export const clearAddressFieldLabelCache = () => {
  Object.keys(cache).forEach((cacheKey) => {
    delete cache[cacheKey];
  });
};

export { DEFAULT_LABELS };
